import _ from "lodash";
import { Divider, Rate } from "rsuite";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import Slider from "react-slick";
import SliderArrow from "../common/SliderArrows";
import { humanizeDuration, StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import { Price } from "../common/Price";

import { InlineIcon } from "@iconify/react";
import { withRouter } from "react-router-dom";
import { genericDescriptionStyles, ratingStyles } from "./helpers";
import jssVariables from "../../jss_setup";
import { Button } from "../common/Button";
import BottomContainer from "../common/BottomContainer";

export const getAddOnItemImgs = (
  item,
  { include_custom_images = true, include_stock_images = true, any = true, size = "l" } = {},
) => {
  var custom_images = [];
  if (include_custom_images || any) {
    custom_images = (item?.data?.custom_images ?? [])
      .filter((img) => img.uuid || img.uid)
      .map((img) => img?.original_image || img?.[`photo_${size}_url`] || img?.photo_sm_url);
  }

  var stock_images = [];
  if (include_stock_images || any) {
    stock_images = _.get(item, "data.image_set", [])
      .filter((img) => _.get(img, "enabled", true))
      .map((img) => _.get(img, `photo_${size}_url`) || img.photo_sm_url);
  }

  if (any) {
    return [...custom_images, ...stock_images];
  } else if (include_custom_images && !include_stock_images) {
    return custom_images;
  } else if (!include_custom_images && include_stock_images) {
    return stock_images;
  } else {
    return [...custom_images, ...stock_images];
  }
};

export const getAddOnImgs = (
  addon,
  {
    include_custom_images = true,
    include_stock_images = true,
    any = true,
    include_items = false,
    size = "l",
  } = {},
) => {
  var imgs = _.reverse(_.sortBy(_.get(addon, "custom_images", []), "main_photo")).map(
    (img) => img[`photo_${size}_url`],
  );

  if (include_items == true) {
    imgs = [
      ...imgs,
      ..._.flattenDeep(
        _.get(addon, "items", []).map((item) => {
          return getAddOnItemImgs(item, {
            include_custom_images,
            include_stock_images,
            any,
            size,
          });
        }),
      ),
    ];
  }

  return imgs.filter((i) => i);
};

const imgCarouselStyles = createUseStyles({
  imgs: (props) => ({
    position: "relative",
    display: "grid",
    width: "100%",
    height: "35vh",
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.imgs", {}),
  }),
  slider: (props) => ({
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    "& .slick-prev": {
      position: "absolute",
      left: jssVariables.normal_gap,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 1,
    },
    "& .slick-next": {
      position: "absolute",
      right: jssVariables.normal_gap,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 1,
    },
    ..._.get(props, "customStyles.slider", {}),
  }),
  img: (props) => ({
    width: "100%",
    height: "35vh",
    objectFit: "cover",
    zIndex: -1,
    borderRadius: [jssVariables.normal_gap, jssVariables.normal_gap, 0, 0],
    ..._.get(props, "customStyles.img", {}),
  }),
  tags: (props) => ({
    alignSelf: "end",
    justifySelf: "start",
    display: "grid",
    gridTemplateColumns: "repeat(10, max-content)",
    overflow: "auto",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    zIndex: 1,
    transform: "translateY(-50%)",
    position: props.isMarketplace ? "absolute" : "relative",
    top: props.isMarketplace ? `calc(${jssVariables.normal_gap} * 2)` : "unset",
    left: props.isMarketplace ? jssVariables.normal_gap : "unset",
    width: "100%",
    ..._.get(props, "customStyles.tags", {}),
  }),
  tag: (props) => ({
    color: jssVariables.colors.text.light,
    background: props.tagColor,
    padding: `calc(${jssVariables.normal_gap} / 4) calc(${jssVariables.normal_gap} / 2) calc(${jssVariables.normal_gap} / 4) calc(${jssVariables.normal_gap} / 2)`,
    borderRadius: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.tag", {}),
  }),
  rating: (props) => ({
    ...ratingStyles,
    position: "absolute",
    transform: "translateY(50%)",
    right: jssVariables.normal_gap,
    bottom: props.isMarketplace ? "-3rem" : "0rem",
    ..._.get(props, "customStyles.rating", {}),
  }),
  provider: (props) => ({
    position: "absolute",
    left: jssVariables.normal_gap,
    bottom: "-2rem",
    filter: "drop-shadow(2px 2px 20px rgba(0, 0, 0, 0.2))",
    ..._.get(props, "customStyles.provider", {}),
  }),
  provider_logo: (props) => ({
    border: `2px solid ${props.baseColor}`,
    borderRadius: "50%",
    width: "4rem",
    height: "4rem",
    objectFit: "scale-down",
    zIndex: 1,
    background: "white",
    padding: ".1rem",
    ..._.get(props, "customStyles.provider_logo", {}),
  }),
  provider_name: (props) => ({
    position: "absolute",
    color: props.baseColor,
    padding: `calc(${jssVariables.normal_gap} / 4) ${jssVariables.normal_gap}`,
    background: "white",
    borderRadius: "0 5px 5px 0",
    marginLeft: `calc(-0.5 * ${jssVariables.normal_gap})`,
    zIndex: -1,
    top: "50%",
    transform: "translateY(-50%)",
    width: "max-content",
    ..._.get(props, "customStyles.provider_name", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    imgs: (props) => ({
      height: "25vh",
      ..._.get(props, "customStyles.imgs.smallscreen", {}),
    }),
    img: (props) => ({
      height: "25vh",
      ..._.get(props, "customStyles.img.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    rating: (props) => ({
      bottom: 0,
      ..._.get(props, "customStyles.rating.bigscreen", {}),
    }),
  },
});
const ImageCarousel = (props) => {
  const { tour, baseColor, tagColor, isMarketplace, showRating, customStyles, excludeTag } = props;

  const { type } = useContext(StyleTypeContext);
  const classes = imgCarouselStyles({
    type,
    baseColor,
    tagColor,
    isMarketplace,
    customStyles,
  });

  var imgs = getAddOnImgs(tour);

  if (!imgs.length) {
    imgs = getAddOnImgs(tour, { include_items: true });
  }

  const settings = {
    arrows: true,
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <SliderArrow icon="fa:angle-right" />,
    prevArrow: <SliderArrow icon="fa:angle-left" />,
  };

  return (
    <div className={classes.imgs}>
      <Slider className={classes.slider} {...settings}>
        {imgs
          .filter((img, idx) => idx < 6)
          .map((img, idx) => (
            <img alt="" src={img} key={idx} className={classes.img} />
          ))}
      </Slider>
      <div className={classes.tags}>
        {[..._.get(tour, "categories", []), ..._.get(tour, "themes", [])]
          .filter((tag) =>
            excludeTag ? _.trim(tag.toLowerCase()) !== _.trim(excludeTag.toLowerCase()) : true,
          )
          .map((cat, idx) => (
            <small key={idx} className={classes.tag}>
              <strong>{_.startCase(cat)}</strong>
            </small>
          ))}
      </div>
      {showRating && type !== "type2" ? (
        <Rate
          className={classes.rating}
          allowHalf
          readOnly
          defaultValue={Number.parseInt(tour.internal_rating)}
          size="xs"
        />
      ) : null}
      {isMarketplace ? (
        <div className={classes.provider}>
          <img
            className={classes.provider_logo}
            src={_.get(
              tour,
              "provider.logo.thumbnail_url",
              "https://apollo-statics.s3.eu-central-1.amazonaws.com/images/image_placeholder.jpg",
            )}
            alt=""
          />
          <span className={classes.provider_name}>
            <strong>{_.get(tour, "provider.name")}</strong>
          </span>
        </div>
      ) : null}
    </div>
  );
};
ImageCarousel.defaultProps = {
  customStyles: {},
  showRating: true,
  baseColor: jssVariables.colors.base_color,
  isMarketplace: false,
};
ImageCarousel.propTypes = {
  customStyles: PropTypes.object,
  showRating: PropTypes.bool,
  baseColor: PropTypes.string.isRequired,
  tour: PropTypes.object.isRequired,
  isMarketplace: PropTypes.bool.isRequired,
  excludeTag: PropTypes.string,
};

const Destination = (props) => {
  const { tour } = props;
  return <span>{_.startCase(_.lowerCase(_.get(tour, "destination.name_en", "")))}</span>;
};
Destination.propTypes = {
  tour: PropTypes.object.isRequired,
};

const descriptionStyles = createUseStyles({
  Description: (props) => ({
    ...genericDescriptionStyles.Description,
    ..._.get(props, "customStyles.Description", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    Description: (props) => ({
      ...genericDescriptionStyles.Description.smallscreen,
      ..._.get(props, "customStyles.Description.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    Description: (props) => ({
      ...genericDescriptionStyles.Description.bigscreen,
      ..._.get(props, "customStyles.Description.bigscreen", {}),
    }),
  },
});
const Description = (props) => {
  const { tour, customStyles } = props;

  const classes = descriptionStyles({ customStyles });

  const {
    i18n: { language },
  } = useContext(TranslationContext);
  const langKey = _.lowerCase(language.split("-")[0]);

  return (
    <p
      className={classes.Description}
      dangerouslySetInnerHTML={{
        __html: _.get(tour, `general_short_description.description_${langKey}`, ""),
      }}
    ></p>
  );
};
Description.propTypes = {
  tour: PropTypes.object.isRequired,
  customStyles: PropTypes.object,
};

const layout = {
  rocs: {
    header: "type1",
    body: "type1",
    bottom: "type3",
  },
};

const productStyles = createUseStyles({
  product: (props) => ({
    display: "grid",
    padding: "0px",
    transition: "box-shadow 0.3s",
    color: jssVariables.colors.text.dark,
    borderRadius: props.borderRadius,
    boxShadow: jssVariables.shadows.normalShadow,
    gridAutoRows: "max-content",
    alignContent: "space-between",
    "&:hover": {
      boxShadow: jssVariables.shadows.hoverShadow,
    },
    "& .rs-panel-body": {
      padding: "0px",
      display: "grid",
      gridGap: jssVariables.normal_gap,
      position: "relative",
      height: "100%",
      background: props.bodyColor,
      paddingBottom: jssVariables.normal_gap,
    },
    ..._.get(props, "customStyles.product", {}),
  }),
  content: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    padding: `0 calc(${jssVariables.normal_gap} * 2)`,
    paddingTop: `calc(${jssVariables.normal_gap} * 4)`,
    paddingBottom: jssVariables.normal_gap,
    gridTemplateRows: "auto auto auto 10rem auto auto auto",
    ..._.get(props, "customStyles.content", {}),
  }),
  destination: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    gridTemplateColumns: "auto 1fr",
    ...props.destinationStyles,
    ..._.get(props, "customStyles.destination", {}),
  }),
  destinationIcon: (props) => props.destinationIconStyles,
  title: (props) => ({
    ...props.titleStyles,
    display: "grid",
    alignItems: "start",
    fontSize: "x-large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.title", {}),
  }),
  subtitle: (props) => ({
    ...props.titleStyles,
    display: "grid",
    alignItems: "start",
    fontSize: "medium",
    fontWeight: "bold",
    ..._.get(props, "customStyles.subtitle", {}),
  }),
  rating: (props) => ({
    ..._.get(props, "customStyles.rating", {}),
  }),
  divider: (props) => ({
    margin: `calc(${jssVariables.normal_gap} / 2) 0`,
    ..._.get(props, "customStyles.divider", {}),
  }),
  metadata: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr auto",
    justifyContent: "end",
    alignItems: "center",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.metadata", {}),
  }),
  price: (props) => ({
    color: props.priceColor,
    justifySelf: "end",
    fontSize: "large !important",
    ..._.get(props, "customStyles.price", {}),
  }),
  bottomContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    ..._.get(props, "customStyles.bottomContainer", {}),
  }),
  bookingSetup: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.bookingSetup", {}),
  }),
  capitalize: (props) => ({
    textTransform: "capitalize",
    ..._.get(props, "customStyles.capitalize", {}),
  }),
  priceText: (props) => ({
    ..._.get(props, "customStyles.priceText", {}),
  }),
  duration: (props) => ({
    ..._.get(props, "customStyles.duration", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    content: (props) => ({
      // gridTemplateRows: "auto auto auto 10vh auto auto auto",
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    title: (props) => ({
      ..._.get(props, "customStyles.title.smallscreen", {}),
    }),
  },
});
const ProductStaticPkg = (props) => {
  const {
    showRating,
    titleFont,
    tour,
    baseColor,
    priceColor,
    tagColor,
    bodyColor,
    borderRadius,
    isMarketplace,
    destinationStyles,
    destinationIconStyles,
    titleStyles,
    excludeTag,
    viewBtnMessage,
    isInLandingPage,
  } = props;

  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const handleBookNow = () => {
    props.history.push(`/tour/MI_${tour.reference}`);
  };

  if (!tour) {
    return null;
  }

  const customStyles = cStyler({
    type,
    brand,
    objName: "productStyles",
  });
  const classes = productStyles({
    product_type: tour.product_type,
    baseColor,
    priceColor,
    bodyColor,
    borderRadius,
    isMarketplace,
    titleFont,
    destinationStyles,
    destinationIconStyles,
    titleStyles,
    customStyles,
  });

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  return (
    <div className={classes.product}>
      <ImageCarousel
        showRating={showRating}
        tour={tour}
        baseColor={baseColor}
        tagColor={tagColor}
        isMarketplace={isMarketplace}
        customStyles={customStyles}
        excludeTag={excludeTag}
      />
      <div className={classes.content}>
        {type !== "type2" && (
          <h5 className={classes.destination}>
            <InlineIcon icon="fa:map-marker" className={classes.destinationIcon} />
            <Destination tour={tour} />
          </h5>
        )}
        <div className={classes.title}>
          <span>{tour.title}</span>
        </div>
        <div className={classes.subtitle}>
          <span>{tour.subtitle}</span>
        </div>
        {showRating && type === "type2" ? (
          <Rate
            className={classes.rating}
            allowHalf
            readOnly
            defaultValue={Number.parseInt(tour.internal_rating)}
            size="xs"
          />
        ) : null}
        <Description tour={tour} customStyles={customStyles} />
        {type !== "type2" && brand !== "rocs" && <Divider className={classes.divider} />}
        <div className={classes.metadata}>
          {type !== "type2" && !["clubseacret", "rocs"].includes(brand) && (
            <div>
              <strong>
                {t("approxDuration")}:{" "}
                {humanizeDuration({
                  duration: _.get(tour, "approximate_duration.hours", 0) * 60 * 60,
                })}
              </strong>
            </div>
          )}
          {brand === "clubseacret" && (
            <div className={classes.bookingSetup}>
              <strong>{tour.pax_setup.max_children === 0 && t("adultsOnly")} </strong>
            </div>
          )}
          {type !== "type2" && brand !== "rocs" && (
            <React.Fragment>
              <div className={classes.price}>
                <small className={classes.capitalize}>{t("from")}</small>{" "}
                <Price
                  price={tour.min_price_from.value}
                  currency={tour.min_price_from.currency}
                  customStyles={customStyles}
                />
                <small>
                  <strong>/{t("person")}</strong>
                </small>
              </div>
            </React.Fragment>
          )}
        </div>
        {brand === "rocs" && (
          <div className={classes.bottomContainer}>
            <div className={classes.priceInfo}>
              <div className={classes.price}>
                <span className={classes.priceText}>{t("from")}</span>{" "}
                <Price
                  price={tour.min_price_from.value}
                  currency={tour.min_price_from.currency}
                  customStyles={customStyles}
                />
                <span className={classes.priceText}>
                  {" / "}
                  {t("person")}
                </span>
              </div>
              <span className={classes.duration}>
                {t("approxDuration")}:{" "}
                {humanizeDuration({
                  duration: _.get(tour, "approximate_duration.hours", 0) * 60 * 60,
                })}
              </span>
            </div>
            <div className={classes.btnContainer}>
              <Button icon="akar-icons:arrow-right" onClick={handleBookNow} />
            </div>
          </div>
        )}
        {type !== "type2" && brand !== "rocs" && (
          <React.Fragment>
            <Divider className={classes.divider} />
            <Button
              text={brand !== "clubseacret" && isInLandingPage ? viewBtnMessage : t("viewEscapes")}
              icon="fa:angle-right"
              onClick={handleBookNow}
            />
          </React.Fragment>
        )}
      </div>
      {type === "type2" && (
        <BottomContainer
          min_price={_.get(tour, "min_price_from.value", 0)}
          currency={_.get(tour, "min_price_from.currency", "EUR")}
          customStyles={customStyles}
          redirectLink={tour.booking_mode === "RD" ? tour.redirect_link : null}
          handleBookNow={handleBookNow}
          product_type={tour.product_type}
          booking_mode={_.get(tour, "booking_mode", "")}
        />
      )}
    </div>
  );
};
ProductStaticPkg.defaultProps = {
  titleFont: "Roboto",
  baseColor: jssVariables.colors.base_color,
  priceColor: jssVariables.colors.base_color,
  tagColor: jssVariables.colors.base_color,
  bodyColor: "unset",
  borderRadius: jssVariables.normal_gap,
  isMarketplace: false,
  viewBtnMessage: "Book Now",
  showRating: true,
  destinationStyles: {
    fontWeight: 400,
    fontFamily: "'Roboto', serif",
  },
  destinationIconStyles: {},
  titleStyles: {},
  isInLandingPage: false,
};
ProductStaticPkg.propTypes = {
  showRating: PropTypes.bool,
  titleFont: PropTypes.string.isRequired,
  borderRadius: PropTypes.string.isRequired,
  baseColor: PropTypes.string.isRequired,
  priceColor: PropTypes.string.isRequired,
  tagColor: PropTypes.string,
  bodyColor: PropTypes.string.isRequired,
  tour: PropTypes.object.isRequired,
  isMarketplace: PropTypes.bool.isRequired,
  viewBtnMessage: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  destinationStyles: PropTypes.object.isRequired,
  destinationIconStyles: PropTypes.object.isRequired,
  excludeTag: PropTypes.string,
  titleStyles: PropTypes.object.isRequired,
  isInLandingPage: PropTypes.bool,
};

export default withRouter(ProductStaticPkg);
